import React, { useState, useEffect, useMemo } from 'react';
import { Search, TrendingUp, FileText, FileSpreadsheet, Loader, AlertCircle } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

function App() {
  const [pattern, setPattern] = useState('');
  const [results, setResults] = useState([]);
  const [searchTime, setSearchTime] = useState(0);
  const [palabras, setPalabras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visitas, setVisitas] = useState(null);

  // Cargar palabras desde archivo JSON externo
  useEffect(() => {
    const cargarPalabras = async () => {
      try {
        setLoading(true);
        const response = await fetch('/palabras.json');

        if (!response.ok) {
          throw new Error('No se pudo cargar el diccionario');
        }

        const data = await response.json();

        // El JSON puede ser un array directo o un objeto con propiedad "palabras"
        const listaPalabras = Array.isArray(data) ? data : (data.palabras || []);

        setPalabras(listaPalabras);
        setError(null);
      } catch (err) {
        console.error('Error cargando palabras:', err);
        setError('No se pudo cargar el diccionario. Asegúrate de que el archivo palabras.json existe.');
      } finally {
        setLoading(false);
      }
    };

    cargarPalabras();
  }, []);

  // Registrar visita y obtener contador
  useEffect(() => {
    const registrarVisita = async () => {
      try {
        const response = await fetch('/contador.php');
        if (response.ok) {
          const data = await response.json();
          setVisitas(data.visitas);
        }
      } catch (err) {
        console.error('Error registrando visita:', err);
      }
    };

    registrarVisita();
  }, []);

  const patternToRegex = (pat) => {
    if (!pat) return null;
    let regexPattern = pat
      .replace(/[.+^${}()|[\]\\]/g, '\\$&')
      .replace(/\*/g, '.*')
      .replace(/\?/g, '.');
    return new RegExp(`^${regexPattern}$`, 'i');
  };

  const searchWords = useMemo(() => {
    const startTime = performance.now();
    
    // MODIFICACIÓN 1: Evitar búsqueda si es solo un asterisco
    if (!pattern.trim() || palabras.length === 0 || pattern.trim() === '*') {
      setSearchTime(0);
      return [];
    }

    const regex = patternToRegex(pattern);
    if (!regex) {
      setSearchTime(0);
      return [];
    }
    const matches = palabras.filter(word => regex.test(word));
    const endTime = performance.now();
    setSearchTime(endTime - startTime);
    
    return matches;
  }, [pattern, palabras]);

  useEffect(() => {
    setResults(searchWords);
  }, [searchWords]);

  const exportToTxt = () => {
    const content = results.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dikzionario_${pattern}_${results.length}_palabras.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToCsv = () => {
    const content = 'Palabra,Longitud\n' +
      results.map(word => `${word},${word.length}`).join('\n');
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dikzionario_${pattern}_${results.length}_palabras.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Pantalla de carga
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <Loader size={48} className="text-primary mb-3" style={{ animation: 'spin 1s linear infinite' }} />
          <h4 className="text-muted">Cargando diccionario...</h4>
          <p className="text-muted small">Esto puede tardar unos segundos</p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Pantalla de error
  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-danger mb-3" />
          <h4 className="text-danger mb-3">Error al cargar el diccionario</h4>
          <p className="text-muted">{error}</p>
          <button className="btn btn-primary mt-3" onClick={() => window.location.reload()}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column">
      <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
        <div className="container">
          {/* MODIFICACIÓN 3: Logo clicable para ir a inicio */}
          <span 
            className="navbar-brand mb-0 h1 fw-bold" 
            style={{ fontSize: '1.5rem', cursor: 'pointer' }}
            onClick={() => setPattern('')}
            title="Ir a inicio"
          >
            <span style={{ color: '#4285f4' }}>d</span>
            <span style={{ color: '#ea4335' }}>i</span>
            <span style={{ color: '#fbbc04' }}>K</span>
            <span style={{ color: '#4285f4' }}>z</span>
            <span style={{ color: '#34a853' }}>i</span>
            <span style={{ color: '#ea4335' }}>o</span>
            <span style={{ color: '#4285f4' }}>n</span>
            <span style={{ color: '#fbbc04' }}>a</span>
            <span style={{ color: '#34a853' }}>r</span>
            <span style={{ color: '#ea4335' }}>i</span>
            <span style={{ color: '#4285f4' }}>o</span>
          </span>
          <span className="badge bg-light text-dark">
            {palabras.length.toLocaleString()} palabras cargadas
          </span>
        </div>
      </nav>

      <div className="flex-grow-1 d-flex align-items-center justify-content-center"
           style={{ minHeight: pattern ? 'auto' : '60vh' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10 col-lg-8">

              {!pattern && (
                <div className="text-center mb-5">
                  {/* MODIFICACIÓN 2: Título grande con colores estilo Google */}
                  <h1 className="display-3 fw-bold mb-3">
                    <span style={{ color: '#4285f4' }}>d</span>
                    <span style={{ color: '#ea4335' }}>i</span>
                    <span style={{ color: '#fbbc04' }}>K</span>
                    <span style={{ color: '#4285f4' }}>z</span>
                    <span style={{ color: '#34a853' }}>i</span>
                    <span style={{ color: '#ea4335' }}>o</span>
                    <span style={{ color: '#4285f4' }}>n</span>
                    <span style={{ color: '#fbbc04' }}>a</span>
                    <span style={{ color: '#34a853' }}>r</span>
                    <span style={{ color: '#ea4335' }}>i</span>
                    <span style={{ color: '#4285f4' }}>o</span>
                  </h1>
                  <p className="text-muted fs-5">Buscador de patrones en palabras del español</p>
                </div>
              )}

              <div className="card shadow-lg border-0 rounded-4 mb-4">
                <div className="card-body p-2">
                  <div className="input-group input-group-lg">
                    <span className="input-group-text bg-white border-0 ps-4">
                      <Search size={24} className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-0 fs-4"
                      placeholder="Introduce un patrón (ej: a*o, c?sa, *para*)"
                      value={pattern}
                      onChange={(e) => setPattern(e.target.value)}
                      autoFocus
                      style={{ boxShadow: 'none' }}
                    />
                  </div>
                </div>
              </div>

              {!pattern && (
                <div className="text-center">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="p-3 bg-light rounded-3">
                        <code className="text-primary fs-5">*</code>
                        <p className="text-muted small mb-0 mt-2">Una o más letras</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 bg-light rounded-3">
                        <code className="text-primary fs-5">?</code>
                        <p className="text-muted small mb-0 mt-2">Exactamente una letra</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-3 bg-light rounded-3">
                        <code className="text-primary fs-5">a-z</code>
                        <p className="text-muted small mb-0 mt-2">Letras específicas</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {pattern && (
                <div className="card border-0 shadow-sm rounded-4 mb-4">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="d-flex align-items-center gap-2">
                          <TrendingUp size={20} className="text-primary" />
                          <span className="fw-semibold">
                            {results.length.toLocaleString()} palabra{results.length !== 1 ? 's' : ''} encontrada{results.length !== 1 ? 's' : ''}
                          </span>
                          <span className="text-muted small">
                            ({searchTime.toFixed(2)}ms)
                          </span>
                        </div>
                      </div>
                      {results.length > 0 && (
                        <div className="col-md-6 text-md-end mt-3 mt-md-0">
                          <div className="btn-group">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={exportToTxt}
                            >
                              <FileText size={16} className="me-1" />
                              TXT
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={exportToCsv}
                            >
                              <FileSpreadsheet size={16} className="me-1" />
                              CSV
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {pattern && (
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-4">
                    {results.length > 0 ? (
                      <div
                        className="results-container"
                        style={{
                          maxHeight: '60vh',
                          overflowY: 'auto',
                          overflowX: 'hidden'
                        }}
                      >
                        <div className="row g-2">
                          {results.map((word, index) => (
                            <div key={index} className="col-6 col-sm-4 col-md-3">
                              <div className="p-2 bg-light rounded-3 text-center"
                                   style={{
                                     transition: 'all 0.2s',
                                     cursor: 'default'
                                   }}>
                                <span className="text-dark fw-medium">{word}</span>
                                <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                                  {word.length} letras
                                </small>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div className="text-muted">
                          <Search size={48} className="mb-3 opacity-25" />
                          <p className="fs-5">No se encontraron palabras para este patrón</p>
                          <p className="small">Intenta con otro patrón diferente</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      <footer className="bg-light border-top py-3 mt-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-muted small">
              <p className="mb-1">
                <strong>diKzionario.com</strong> - Buscador de patrones en palabras del español
              </p>
              <p className="mb-0">
                Usa <code>*</code> para múltiples letras, <code>?</code> para una letra exacta
                {visitas && (
                  <span className="ms-3">
                    • <strong>{visitas.toLocaleString()}</strong> visitas totales
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        .results-container::-webkit-scrollbar {
          width: 8px;
        }
        .results-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .results-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        .results-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
}

export default App;
